/**
 * ExternalActionCard.EmbeddedResearchBrief — embedded research brief for
 * external action cards.
 *
 * Renders an `ahoy_research` entity's brief inline (or collapsed in a
 * `CollapsibleSection` when shown in the sidebar surface), with a
 * "compose email" action wired to the entity's primary contact. Falls back
 * to the caller-supplied fallback content while loading or on error.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useProposalSidebar } from '@/contexts/ActionSidebarContext';
import type { ProposalSurface } from '@/contexts/ProposalSurfaceContext';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import { ResearchDetail } from '@/lib/crm/record/research/ResearchDetail';
import { extractResearchData } from '@/lib/crm/record/research/useResearch';
import { CollapsibleSection } from '@/lib/crm/record/sections/collapsible-section';
import {
  crmObjectListSentinelQuery,
  useCrmObject,
  useStoreBackedList,
} from '@/lib/crm/store';
import { apiPatch } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import { quickEmailProposal, useCreateProposal } from '@/lib/proposals';
import { getPropertyValue } from '@/types/crm';

export interface EmbeddedResearchBriefProps {
  entityId: string;
  orgId: number;
  proposalId: number | undefined;
  surface: ProposalSurface;
  fallback: ReactNode;
}

export function EmbeddedResearchBrief({
  entityId,
  orgId,
  proposalId: _proposalId,
  surface,
  fallback,
}: EmbeddedResearchBriefProps) {
  // All hooks before any early returns
  const { object, isLoading, error } = useCrmObject(
    orgId,
    'ahoy_research',
    entityId
  );
  const research = useMemo(
    () => (object ? extractResearchData(object) : null),
    [object]
  );
  const createProposal = useCreateProposal(orgId);
  const { openProposal } = useProposalSidebar();
  const { user } = useAuthInfo();
  const queryClient = useQueryClient();

  // Fetch associated contact to resolve email for compose
  const contactsQuery = useQuery({
    ...crmObjectListSentinelQuery(orgId, 'ahoy_contact', {
      associated_with_entity_id: entityId,
    }),
    enabled: !!orgId && !!entityId,
    staleTime: 5 * 60 * 1000,
  });
  const contactsList = useStoreBackedList(contactsQuery);
  const contactData = useMemo(() => {
    // Prefer a contact that has a usable email so compose-email isn't
    // incorrectly disabled when the first result lacks one.
    const withEmail = contactsList.results.find(contact => {
      const email = getPropertyValue<string | string[]>(
        contact.properties?.ahoy_email
      );
      if (Array.isArray(email)) {
        return email.some(e => typeof e === 'string' && e.trim().length > 0);
      }
      return typeof email === 'string' && email.trim().length > 0;
    });
    return withEmail ?? contactsList.results[0] ?? null;
  }, [contactsList.results]);

  const contactEmailRaw = contactData
    ? getPropertyValue<string | string[]>(contactData.properties?.ahoy_email)
    : null;
  const isUsableEmail = (e: unknown): e is string =>
    typeof e === 'string' && e.trim().length > 0;
  let contactEmail: string | undefined;
  if (Array.isArray(contactEmailRaw)) {
    contactEmail = contactEmailRaw.find(isUsableEmail)?.trim();
  } else if (isUsableEmail(contactEmailRaw)) {
    contactEmail = contactEmailRaw.trim();
  }

  const handleComposeEmail = useCallback(
    async (data: {
      component_id: string;
      subject: string;
      body: string;
      context_label: string;
    }) => {
      if (!contactEmail || !user?.id) {
        return;
      }
      if (!data.subject?.trim() || !data.body?.trim()) {
        toast.error('Conversation starter is missing subject or body content');
        return;
      }
      try {
        const request = quickEmailProposal({
          title: `Email: ${data.subject}`,
          description: `Conversation starter from research: ${data.context_label}`,
          assignedUserId: user.id,
          sourceTypeName: 'ahoy_research',
          sourceId: entityId,
          // `ahoy_research_starter` (an INTERACTIVE channel) keeps this proposal
          // out of the inbox. The research brief is its home surface — we open
          // it in the slideout immediately and link it back to the brief via
          // the apiPatch below, so a duplicate inbox entry would be noise.
          sourceContext: {
            channel: 'ahoy_research_starter',
            account_identifier: user.email,
          },
          to: [contactEmail],
          subject: data.subject,
          body: data.body,
        });
        const proposal = await createProposal.mutateAsync(request);
        openProposal(proposal.id);
        try {
          await apiPatch(
            `/api/orgs/${orgId}/research/${entityId}/components/${data.component_id}/`,
            { proposal_id: proposal.id }
          );
        } catch (patchError) {
          logger.error(
            'Failed to store proposal_id on conversation starter component',
            patchError instanceof Error ? patchError : undefined,
            {
              entity_id: entityId,
              component_id: data.component_id,
              proposal_id: proposal.id,
            }
          );
        }
        queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objectDetail(orgId, 'ahoy_research', entityId),
        });
      } catch (err) {
        logger.error(
          'Failed to create email proposal from research brief',
          err instanceof Error ? err : undefined,
          {
            entity_id: entityId,
            context_label: data.context_label,
            has_email: !!contactEmail,
          }
        );
        toast.error("We couldn't create the email draft. Try again.");
      }
    },
    [
      contactEmail,
      user,
      entityId,
      orgId,
      createProposal,
      openProposal,
      queryClient,
    ]
  );

  const handleOpenProposal = useCallback(
    (pid: number) => {
      openProposal(pid);
    },
    [openProposal]
  );

  if (isLoading) {
    return (
      <div className='space-y-2 animate-pulse'>
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-3/4' />
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-1/2' />
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-5/6' />
      </div>
    );
  }

  if (error || !research) {
    return fallback;
  }

  const researchContent = (
    <ResearchDetail
      research={research}
      object={object}
      onComposeEmail={contactEmail ? handleComposeEmail : undefined}
      onOpenProposal={handleOpenProposal}
      isComposing={createProposal.isPending}
    />
  );

  if (surface === 'sidebar') {
    return (
      <CollapsibleSection title='Research brief' defaultExpanded>
        {researchContent}
      </CollapsibleSection>
    );
  }

  return researchContent;
}
